import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { ref, uploadString, getDownloadURL } from "@firebase/storage";
import { storage } from "../../main-component/Firebase";
import "./style.css";

const API = process.env.REACT_APP_API_BASE;
const ADMIN_EMAILS = [
  process.env.REACT_APP_ADMIN,
  process.env.REACT_APP_ARI_ADMIN,
].filter(Boolean);

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const round2 = (v) => Math.round(v * 100) / 100;

const PAN_STEP = 15;   // px per arrow click
const PAN_MAX = 300;  // max offset in either direction

// ─── Window pre-fetch helpers ─────────────────────────────────────────────────
// public/index.html fires a fetch for /api/imageSettings/get BEFORE the React
// bundle loads and stores the result in window.__SITE_DESIGN__.
// This means useState initialisers can read the correct saved values
// synchronously — no flash for any visitor, including first-time visitors.

function getPreFetched() {
  return window.__SITE_DESIGN__ || null;
}

function updatePreFetched(patch) {
  window.__SITE_DESIGN__ = { ...(window.__SITE_DESIGN__ || {}), ...patch };
}

// ─── Hook ────────────────────────────────────────────────────────────────────

function useImageZoomPan(settingKey, scaleMin, scaleMax, imageKey, initialSrc, disabled = false) {
  const email = useSelector((s) => s?.user?.user?.email);
  const isAdmin = !!email && ADMIN_EMAILS.includes(email);

  // ── Initialise from the pre-fetched window object.
  //    If the fetch has already resolved (the common case, because the JS
  //    bundle is much larger than the settings JSON), we get the correct value
  //    immediately with zero flash. If not, we start at the default (1/0/0)
  //    and update when the promise resolves below.
  const [scale, setScaleRaw] = useState(() => {
    const c = getPreFetched();
    const s = c ? Number(c[settingKey]) : NaN;
    return !isNaN(s) && s > 0 ? round2(clamp(s, scaleMin, scaleMax)) : 1;
  });
  const [offsetX, setOffsetXRaw] = useState(() => {
    const c = getPreFetched();
    const x = c ? Number(c[`${settingKey}X`]) : NaN;
    return !isNaN(x) ? x : 0;
  });
  const [offsetY, setOffsetYRaw] = useState(() => {
    const c = getPreFetched();
    const y = c ? Number(c[`${settingKey}Y`]) : NaN;
    return !isNaN(y) ? y : 0;
  });
  // ── Image source — persisted in DB so every visitor (incl. first-time)
  //    sees the chosen image immediately with no fallback flash.
  const [src, setSrcRaw] = useState(() => {
    const c = getPreFetched();
    const s = imageKey && c ? c[imageKey] : null;
    return typeof s === "string" && s ? s : initialSrc || "";
  });
  const [savedSrc, setSavedSrc] = useState(() => {
    const c = getPreFetched();
    const s = imageKey && c ? c[imageKey] : null;
    return typeof s === "string" && s ? s : initialSrc || "";
  });
  const [uploading, setUploading] = useState(false);
  const [saved, setSaved] = useState({ scale: 1, x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  // Suppress CSS transition until saved values are applied — prevents the
  // visible "distort then snap" animation when the pre-fetch resolves after mount.
  const [settled, setSettled] = useState(() =>
    window.__SITE_DESIGN__ !== null && window.__SITE_DESIGN__ !== undefined
  );

  useEffect(() => {
    if (disabled) { setSettled(true); return; }
    let live = true;

    function applyDoc(doc) {
      if (!live || !doc) return;

      const s = Number(doc[settingKey]);
      const x = Number(doc[`${settingKey}X`]);
      const y = Number(doc[`${settingKey}Y`]);

      const safeS = !isNaN(s) && s > 0 ? round2(clamp(s, scaleMin, scaleMax)) : 1;
      const safeX = !isNaN(x) ? x : 0;
      const safeY = !isNaN(y) ? y : 0;

      setScaleRaw(safeS);
      setOffsetXRaw(safeX);
      setOffsetYRaw(safeY);
      setSaved({ scale: safeS, x: safeX, y: safeY });

      if (imageKey) {
        const ds = doc[imageKey];
        if (typeof ds === "string" && ds) {
          setSrcRaw(ds);
          setSavedSrc(ds);
        }
      }

      // Keep the in-memory store up-to-date for other components
      updatePreFetched(doc);
      // Values are now correct — enable smooth transition for user interactions
      setSettled(true);
    }

    if (window.__SITE_DESIGN__ !== null && window.__SITE_DESIGN__ !== undefined) {
      // Pre-fetch already resolved — apply synchronously. React bails out
      // automatically if all resulting state values are identical.
      applyDoc(window.__SITE_DESIGN__);
    } else if (window.__SITE_DESIGN_PROMISE__) {
      // Pre-fetch still in flight — await it instead of making a duplicate call
      window.__SITE_DESIGN_PROMISE__.then((doc) => {
        if (live) applyDoc(doc);
      });
    } else {
      // Fallback: no pre-fetch in HTML (e.g. very old cached HTML) — call API directly
      axios
        .get(`${API}/imageSettings/get`)
        .then((res) => { if (live) applyDoc(res.data || {}); })
        .catch(() => {});
    }

    return () => { live = false; };
  }, [settingKey, scaleMin, scaleMax, imageKey, disabled]);

  const setScale = useCallback(
    (v) => {
      const n = Number(v);
      if (!isNaN(n)) setScaleRaw(round2(clamp(n, scaleMin, scaleMax)));
    },
    [scaleMin, scaleMax]
  );

  const panBy = useCallback((dx, dy) => {
    setOffsetXRaw((x) => clamp(x + dx, -PAN_MAX, PAN_MAX));
    setOffsetYRaw((y) => clamp(y + dy, -PAN_MAX, PAN_MAX));
  }, []);

  const resetAll = useCallback(() => {
    setScaleRaw(1);
    setOffsetXRaw(0);
    setOffsetYRaw(0);
  }, []);

  // ── Upload a new image: convert to base64, push to Firebase Storage,
  //    then IMMEDIATELY persist the URL to Firestore so every visitor
  //    (including first-time) sees the new photo straight away — no
  //    manual "Salvează" click required for image changes.
  const uploadSrc = useCallback(
    async (file) => {
      if (!file || !imageKey || disabled) return;
      setUploading(true);
      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const imageRef = ref(storage, `siteDesign/${imageKey}`);
        await uploadString(imageRef, base64, "data_url");
        const url = await getDownloadURL(imageRef);

        // ① Update local display immediately
        setSrcRaw(url);

        // ② Persist to Firestore so refresh shows the new photo
        const config = process.env.REACT_APP_TOKEN
          ? { headers: { Authorization: `Basic ${process.env.REACT_APP_TOKEN}` } }
          : {};
        await axios.put(`${API}/imageSettings/save`, { [imageKey]: url }, config);

        // ③ Mark as saved — dirty flag won't trigger for image alone
        setSavedSrc(url);
        updatePreFetched({ [imageKey]: url });

        toast.success("Imaginea a fost salvată automat!");
      } catch (err) {
        toast.error("Eroare la încărcarea sau salvarea imaginii");
      } finally {
        setUploading(false);
      }
    },
    [imageKey, disabled]
  );

  const save = useCallback(async () => {
    if (!isAdmin || disabled) return;
    setSaving(true);
    try {
      const payload = {
        [settingKey]:       scale,
        [`${settingKey}X`]: offsetX,
        [`${settingKey}Y`]: offsetY,
      };
      if (imageKey && src) payload[imageKey] = src;
      const config = process.env.REACT_APP_TOKEN
        ? { headers: { Authorization: `Basic ${process.env.REACT_APP_TOKEN}` } }
        : {};

      await axios.put(`${API}/imageSettings/save`, payload, config);

      setSaved({ scale, x: offsetX, y: offsetY });
      if (imageKey) setSavedSrc(src);

      updatePreFetched({
        [settingKey]:       scale,
        [`${settingKey}X`]: offsetX,
        [`${settingKey}Y`]: offsetY,
        ...(imageKey && src ? { [imageKey]: src } : {}),
      });

      // toast.success("Setările imaginii au fost salvate!");
    } catch (err) {
      toast.error("Eroare la salvarea setărilor imaginii");
    } finally {
      setSaving(false);
    }
  }, [isAdmin, disabled, settingKey, scale, offsetX, offsetY, imageKey, src]);

  const dirty =
    round2(scale) !== round2(saved.scale) ||
    offsetX !== saved.x ||
    offsetY !== saved.y ||
    (imageKey ? src !== savedSrc : false);

  return { scale, setScale, offsetX, offsetY, panBy, resetAll, save, saving, isAdmin, dirty, src, uploadSrc, uploading, settled };
}

// ─── Icons ───────────────────────────────────────────────────────────────────

const IconLupa = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.35-4.35" />
    <path d="M11 8v6M8 11h6" />
  </svg>
);

const IconClose = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

// ─── Control Panel ───────────────────────────────────────────────────────────

const ControlPanel = ({
  scale, scaleMin, scaleMax, scaleStep,
  saving, dirty,
  onScaleChange, onPan, onReset, onSave,
  canChangeImage, uploading, onUpload,
  hasDesktopTab = false,
  adminTab = "all",
  onTabChange,
}) => {
  const fileInputRef = useRef(null);
  return (
  <div className="aiz-panel" role="dialog" aria-label="Editare imagine">

    {/* ── Device tabs (Desktop-only option) ── */}
    {hasDesktopTab && (
      <div className="aiz-panel__device-tabs">
        <button
          type="button"
          className={`aiz-panel__device-tab${adminTab === "all" ? " aiz-panel__device-tab--active" : ""}`}
          onClick={() => onTabChange("all")}
        >
          Tot
        </button>
        <button
          type="button"
          className={`aiz-panel__device-tab${adminTab === "desktop" ? " aiz-panel__device-tab--active" : ""}`}
          onClick={() => onTabChange("desktop")}
        >
          Desktop
        </button>
      </div>
    )}

    {/* ── Change image section ── */}
    {canChangeImage && (
      <div className="aiz-panel__section">
        <span className="aiz-panel__label">Imagine</span>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png, image/jpeg, image/gif, image/webp"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files && e.target.files[0];
            if (f) onUpload(f);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          className="aiz-panel__photo-btn"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          disabled={uploading}
        >
          {uploading ? (
            "Se încarcă..."
          ) : (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9a2 2 0 0 1 2-2h.93a2 2 0 0 0 1.66-.9l.82-1.2A2 2 0 0 1 10.07 3h3.86a2 2 0 0 1 1.66.9l.82 1.2a2 2 0 0 0 1.66.9H19a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <circle cx="12" cy="13" r="3" />
              </svg>
              Schimbă poza
            </>
          )}
        </button>
      </div>
    )}

    {/* ── Zoom section ── */}
    <div className="aiz-panel__section">
      <span className="aiz-panel__label">Zoom</span>
      <div className="aiz-panel__zoom-row">
        <button
          type="button"
          className="aiz-panel__sq-btn"
          onClick={() => onScaleChange(round2(scale - scaleStep))}
          disabled={scale <= scaleMin}
          aria-label="Micșorează"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M5 12h14" /></svg>
        </button>
        <input
          type="range"
          className="aiz-panel__range"
          min={scaleMin}
          max={scaleMax}
          step={scaleStep}
          value={scale}
          onChange={(e) => onScaleChange(e.target.value)}
          aria-label="Zoom"
        />
        <button
          type="button"
          className="aiz-panel__sq-btn"
          onClick={() => onScaleChange(round2(scale + scaleStep))}
          disabled={scale >= scaleMax}
          aria-label="Mărește"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
        </button>
        <span className="aiz-panel__pct">{Math.round(scale * 100)}%</span>
      </div>
    </div>

    {/* ── Pan section ── */}
    <div className="aiz-panel__section">
      <span className="aiz-panel__label">Poziție</span>
      <div className="aiz-panel__pan-grid">
        {/* row 1 */}
        <span />
        <button type="button" className="aiz-panel__pan-btn" onClick={() => onPan(0, -PAN_STEP)} aria-label="Sus">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
        </button>
        <span />
        {/* row 2 */}
        <button type="button" className="aiz-panel__pan-btn" onClick={() => onPan(-PAN_STEP, 0)} aria-label="Stânga">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
        </button>
        <button type="button" className="aiz-panel__pan-btn aiz-panel__pan-btn--center" onClick={onReset} aria-label="Centru" title="Resetează poziția">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><circle cx="12" cy="12" r="3" /><path d="M12 2v4M12 18v4M2 12h4M18 12h4" /></svg>
        </button>
        <button type="button" className="aiz-panel__pan-btn" onClick={() => onPan(PAN_STEP, 0)} aria-label="Dreapta">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
        </button>
        {/* row 3 */}
        <span />
        <button type="button" className="aiz-panel__pan-btn" onClick={() => onPan(0, PAN_STEP)} aria-label="Jos">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M19 12l-7 7-7-7" /></svg>
        </button>
        <span />
      </div>
    </div>

    {/* ── Footer ── */}
    <div className="aiz-panel__footer">
      <button type="button" className="aiz-panel__ghost-btn" onClick={onReset}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" />
        </svg>
        Reset
      </button>
      <button type="button" className="aiz-panel__save-btn" onClick={onSave} disabled={saving || !dirty}>
        {saving ? "Se salvează..." : "Salvează"}
      </button>
    </div>
  </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

/**
 * Wraps an image with an admin-only FAB that opens a zoom + pan panel.
 * The chosen values persist to the shared `siteDesign` settings doc so
 * every visitor sees the same framing.
 *
 * variant="frame"  → small framed images (NewAbout, NewCoaching circles)
 * variant="cover"  → full-bleed background images (NewHero slider)
 */
const AdminImageZoom = ({
  settingKey,
  src,
  alt = "",
  variant = "frame",
  frameClassName = "",
  imgClassName = "",
  imageKey = "",
  min = 0.5,
  max = 3,
  step = 0.1,
  desktopSettingKey = "",
  desktopImageKey = "",
}) => {
  const hasDesktop = !!desktopSettingKey;

  // Always call both hooks (React rules — number of hooks must be constant).
  // When hasDesktop is false, the second hook runs with disabled=true so it
  // never fetches, saves or uploads — it just holds default 1/0/0 values.
  const hook = useImageZoomPan(settingKey, min, max, imageKey, src, false);
  const desktopHook = useImageZoomPan(
    hasDesktop ? desktopSettingKey : settingKey,
    min, max,
    hasDesktop ? desktopImageKey : imageKey,
    src,
    !hasDesktop
  );

  const { isAdmin } = hook;

  const [panelOpen, setPanelOpen] = useState(false);
  const [adminTab, setAdminTab] = useState("all");  // "all" | "desktop"

  // Track whether the current viewport is desktop-sized.
  // Visitors on desktop will see desktop-specific settings when saved.
  const [isDesktopVP, setIsDesktopVP] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024
  );
  useEffect(() => {
    const h = () => setIsDesktopVP(window.innerWidth >= 1024);
    window.addEventListener("resize", h, { passive: true });
    return () => window.removeEventListener("resize", h);
  }, []);

  // Has the admin saved desktop-specific settings at least once?
  const hasDesktopSaved = hasDesktop &&
    window.__SITE_DESIGN__ != null &&
    window.__SITE_DESIGN__[desktopSettingKey] !== undefined;

  // Which hook the PANEL controls (what the admin edits via the UI).
  const controlHook = hasDesktop && adminTab === "desktop" ? desktopHook : hook;

  // Which hook drives the DISPLAYED image (what every visitor sees).
  // When the panel is open we always show what the admin is currently editing.
  const displayHook = panelOpen
    ? controlHook
    : (isDesktopVP && hasDesktopSaved ? desktopHook : hook);

  // For "cover" variant (hero), use object-position for pan so the image
  // always fills its container — translate would reveal the white background.
  // For "frame" variant (circles), translate is fine because the frame clips.
  const isCover = variant === "cover";
  const imgStyle = isCover
    ? {
        objectPosition: `calc(50% + ${displayHook.offsetX}px) calc(50% + ${displayHook.offsetY}px)`,
        transform: `scale(${displayHook.scale})`,
        transformOrigin: "center center",
        transition: displayHook.settled
          ? "transform 0.15s ease-out, object-position 0.15s ease-out"
          : "none",
      }
    : {
        transform: `translate(${displayHook.offsetX}px, ${displayHook.offsetY}px) scale(${displayHook.scale})`,
        transformOrigin: "center center",
        transition: displayHook.settled ? "transform 0.15s ease-out" : "none",
      };

  const displaySrc = displayHook.src || src;

  const fab = (
    <button
      type="button"
      className={`aiz-fab${panelOpen ? " aiz-fab--open" : ""}`}
      onClick={() => setPanelOpen((o) => !o)}
      aria-label={panelOpen ? "Închide editare" : "Editare imagine"}
      title={panelOpen ? "Închide" : "Editare imagine"}
    >
      {panelOpen ? <IconClose /> : <IconLupa />}
    </button>
  );

  const panel = panelOpen ? (
    <ControlPanel
      scale={controlHook.scale}
      scaleMin={min}
      scaleMax={max}
      scaleStep={step}
      saving={controlHook.saving}
      dirty={controlHook.dirty}
      onScaleChange={controlHook.setScale}
      onPan={controlHook.panBy}
      onReset={controlHook.resetAll}
      onSave={controlHook.save}
      canChangeImage={adminTab === "desktop" ? !!desktopImageKey : !!imageKey}
      uploading={controlHook.uploading}
      onUpload={controlHook.uploadSrc}
      hasDesktopTab={hasDesktop}
      adminTab={adminTab}
      onTabChange={setAdminTab}
    />
  ) : null;

  /* ── Cover (hero/slider) ── */
  if (variant === "cover") {
    return (
      <>
        <img
          className={imgClassName}
          src={displaySrc}
          alt={alt}
          draggable="false"
          fetchpriority="high"
          loading="eager"
          style={imgStyle}
        />
        {isAdmin && (
          <div className="aiz-cover-hud">
            {fab}
            {panel}
          </div>
        )}
      </>
    );
  }

  /* ── Frame (circles, etc.) ── */
  return (
    <div className={`aiz${isAdmin ? " aiz--admin" : ""}`}>
      <div className={`aiz__clip ${frameClassName}`}>
        <img
          className={imgClassName}
          src={displaySrc}
          alt={alt}
          draggable="false"
          style={imgStyle}
        />
      </div>
      {isAdmin && (
        <>
          {fab}
          {panel}
        </>
      )}
    </div>
  );
};

export default AdminImageZoom;